// Modules
import { AxiosInstance, AxiosResponse, AxiosRequestConfig } from "axios"

// Constants
import PATHS from "~constants/paths"
import { handleApiErrorMessage } from "~constants/errorMessages"

// Services
import AuthService from "~services/AuthService"

// Utils
import { getMemoryToken, setMemoryToken } from "~utils/auth-token"
import { HTTP_STATUS } from "~constants/httpStatus"

export const httpTokenInjectorInterceptor = (request: AxiosRequestConfig) => {
  if (!request.headers) {
    request.headers = {}
  }
  const token = getMemoryToken()
  if (token) {
    request.headers["Authorization"] = `Bearer ${token}`
  }
  request.headers["stockperks-client-platform"] = "perk-central"
  request.headers["stockperks-client-version"] =
    import.meta.env.VITE_APP_VERSION || "v0.0"
  return request
}

// Interceptor to handle success responses
export const httpSuccessInterceptor = (
  response: AxiosResponse,
): AxiosResponse => {
  return response
}

// Interceptor to handle error responses
export const httpErrorInterceptor = (error: any): Promise<any> => {
  return handleApiErrorMessage(error)
}

// Interceptor to handle the refresh token
export const httpRefreshTokenInterceptor = (
  error: any,
  instance: AxiosInstance,
): Promise<any> => {
  const originalRequest = error.config

  if (error?.response?.status === 401 && !originalRequest._retry) {
    originalRequest._retry = true

    return AuthService.spaRefresh()
      .then(data => {
        setMemoryToken(data.access_token)
        originalRequest.headers["Authorization"] = `Bearer ${data.access_token}`
        return instance.request(originalRequest)
      })
      .catch(err => {
        setMemoryToken(null)
        window.location.href = PATHS.login
        return Promise.reject(err)
      })
  }

  // Reject
  return Promise.reject(error)
}

// Interceptor to automatically logout the user in case there's no more request retries
export const logoutInterceptor = (error: any) => {
  if (error && error.statusCode === HTTP_STATUS.UNAUTHORIZED) {
    // Using window.location for navigation outside React components
    window.location.href = PATHS.logout
  }

  // Always keep the chain rejected so callers can handle the error.
  // Returning undefined here would turn the rejection into a resolution,
  // making `const { data } = await Api.xxx()` throw a misleading
  // "Cannot destructure property 'data'" error instead of surfacing the
  // real API error message.
  return Promise.reject(error)
}
